import {
  popupStore,
  userInfoStore,
  gameResultStore,
  gameConfigStore,
  applicationStore,
  userWinCountStore,
  roundInfoStore,
  freeBetStore,
  ApiServices,
  ApiServiceTypes,
  DefaultQueryParams,
  isMobile,
  notificationStore,
  parseQuery,
  RoundStatus,
  tableBetHistoryStore,
  loadingStore,
  platformMessageStore,
} from '@pascal/pascal-core';
import { gameStore } from '../store/gameStore';
import { CARDS } from '../enums/cardTypes';
import { roundsWinBetTypes } from '../store/roundsWinBetTypes';
import { doBetStore } from '../store/doBetStore';
import { cardsStore } from '../store/cardsStore';
import { soundService, soundTypes } from './SoundService';
import { winningAmountStore } from '../store/winningAmountStore';
import { limitsAndPayoutsStore } from '../store/limitsAndPayoutsStore';
import { MobileShuffleAnimation, WebShuffleAnimation } from '../core/constants/events';
import { gameLiveConnection } from './GameConnection';
import { betResponseCodeByName } from '../enums/betResponseCode';

const animationsTimeOutId = [];
let shuffleAnimationTimeOutId;
let endShuffleAnimationTimeOutId;
let burnedCardsAnimationTimeOutId;
let endBurnedCardsAnimationTimeOutId;

class TurboGameService {
  onGetInitialStateResult({
    rate,
    cards,
    limits,
    balance,
    precision,
    roundInfo,
    lastResults,
    currencyCode,
    gameConfiguration,
    isTestPlatform,
    currentBet,
    gameTypeInfo,
    underMaintenance,
    userFreebets,
  }) {
    this.handleResetDataInitial();
    const {
      [DefaultQueryParams.CurrencySymbol]: currencySymbol,
      [DefaultQueryParams.UserId]: userId,
    } = parseQuery(window.location.search);
    freeBetStore.setIsFreeChip(true);
    if (underMaintenance) {
      popupStore.startUnderMaintenanceFunctional(underMaintenance);
    }
    userInfoStore.setUserInfo({
      rate,
      userId,
      balance,
      precision,
      currencyCode,
      currencySymbol,
      demoBalance: 10000,
    });
    const alreadyOpenedCards = cards
      .filter(
        ({ card, roundId }) =>
          card !== CARDS.CUTTING_CARD && roundId !== roundInfo?.roundId && roundId !== 0
      )
      .reverse();
    const nextCards = cards
      .filter(({ roundId }) => !roundId || (roundInfo && roundId === roundInfo?.roundId))
      .reverse();
    const { limitsAndPayouts } = gameConfiguration;
    cardsStore.setOpenedCardsCount(alreadyOpenedCards.length);
    limitsAndPayoutsStore.setLimitsAndPayouts(limitsAndPayouts);
    roundsWinBetTypes.setWinBetTypes(lastResults || []);
    gameResultStore.setGameResults(nextCards);
    gameResultStore.setAdditionalResults(alreadyOpenedCards);
    gameConfigStore.setLimits(limits);
    applicationStore.setIsTestPlatform(isTestPlatform);
    cardsStore.setCuttingCardPlace(gameTypeInfo.cuttingCardPlace);
    doBetStore.setQuickBets(limits.quickBets);
    if (!freeBetStore.activeFreeBet.id) {
      doBetStore.setSelectedChip(limits.quickBets[0]);
    }
    loadingStore.updateLoading({ isGameInitialLoaded: true });
    gameStore.setGameTypeId();
    if (roundInfo && roundInfo.status === RoundStatus.Started && !+gameTypeInfo.shuffledSeconds) {
      if (currentBet) {
        if (currentBet.freeBetId) {
          freeBetStore.setAwardedFreeBet(
            userFreebets.find((item) => item.id === currentBet.freeBetId)
          );
        }
        doBetStore.setBetsModel({
          betModel: currentBet.bet.markets.map((item) => {
            const { winAmount, ...all } = item;
            return all;
          }),
        });
      }
      gameStore.setIsBetsClosed(true);
      if (gameTypeInfo.cuttingCardPlace < 0) {
        cardsStore.setIsCuttingCardOpen(true);
      }
      roundInfo.roundCards.forEach((openedCard) => {
        cardsStore.updateTableCards(openedCard);
      });
      cardsStore.setBankerTotalPoints(roundInfo.roundData.bankerTotalPoint);
      cardsStore.setPlayerTotalPoints(roundInfo.roundData.playerTotalPoint);
    }
    if (roundInfo) {
      roundInfoStore.setRoundId(roundInfo.roundId);
    }
    if (roundInfo && roundInfo.status === RoundStatus.OnFinish) {
      if (currentBet.length) {
        doBetStore.setBetsModel({
          betModel: currentBet.bet.betData,
        });
        winningAmountStore.setWinningAmount(currentBet.bet.winAmount);
        winningAmountStore.setLastWin(currentBet.bet.winAmount);
        if (roundInfo.roundData) {
          winningAmountStore.setWinType(roundInfo.roundData.winType);
          winningAmountStore.setIsPairWin(roundInfo.roundData.isPairWin);
        }
        setTimeout(() => {
          this.handleResetEndedRoundData();
        }, 5000);
      }
    }
    // if (gameTypeInfo.shuffledSeconds) {
    //   const { shuffledSeconds } = gameTypeInfo;
    //   gameStore.setIsBetsClosed(true);
    //   this.handleResetEndedRoundData();
    //   this.handleShuffleAnimationInitial({ burnedCardsInfo, shuffledSeconds });
    // }
    popupStore.close();
  }

  handleDoBetResult({ betResult, lastResults, shuffleCardsInfo, finishedRound }) {
    doBetStore.setBetsModel({
      betModel: betResult.betData,
    });
    roundsWinBetTypes.setWinBetTypes(lastResults);
    winningAmountStore.setWinType(finishedRound.roundData.winType);
    if (betResult.winAmount) {
      winningAmountStore.setWinningAmount(betResult.winAmount);
      winningAmountStore.setLastWin(betResult.winAmount);
      setTimeout(() => {
        gameLiveConnection.getBalance();
      }, 200);
      this.handleDoBetResultFunctionality(shuffleCardsInfo, 1000);
    } else {
      this.handleDoBetResultFunctionality(shuffleCardsInfo, 500);
    }
  }

  handleDoBetResultValidation({ betResult: { responseCode } }) {
    doBetStore.resetBetsModels();
    !platformMessageStore.isShowAllNotification
      ? null
      : notificationStore.setNotificationType(responseCode);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
    gameStore.setIsBetsClosed(false);
  }

  sendBet() {
    gameStore.setIsBetsClosed(true);
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    const { betsModels } = doBetStore;
    const { activeFreeBet } = freeBetStore;

    const betData = betsModels[betsModels.length - 1]?.betModel;
    if (betData) {
      gameLiveConnection.doBet(
        activeFreeBet.id
          ? {
              betData,
              freeBetId: activeFreeBet.id,
              gameId,
            }
          : {
              betData,
              gameId: gameId,
            }
      );
    }
  }

  handleCardsResult(resultReportData) {
    const { cardsResult } = resultReportData;
    const lastAndNextResults = cardsResult.reverse().reduce(
      (acc, cur) => {
        if (cur.roundId) {
          acc.alreadyOpenedCards.push(cur);
        } else {
          acc.nextCards.push(cur);
        }
        return acc;
      },
      {
        alreadyOpenedCards: [],
        nextCards: [],
      }
    );
    gameResultStore.setGameResults(lastAndNextResults.nextCards);
    gameResultStore.setAdditionalResults(lastAndNextResults.alreadyOpenedCards);
    // this.handleResetEndedRoundData();
    this.handleShuffleStarting(resultReportData, true);
  }

  sendShuffleCards() {
    gameStore.setIsBetsClosed(true);
    gameLiveConnection.shuffleCards();
  }

  handleTurboBetAccepted({ responseCode, amount, balance, roundId, freeBetId }) {
    if (responseCode === betResponseCodeByName.Success) {
      roundInfoStore.setRoundId(roundId);
      doBetStore.setReBet(doBetStore.lastBetModel.betModel);
      doBetStore.setReBetAmount(amount);
      if (!freeBetId) {
        userInfoStore.setBalance(balance);
      }
    } else {
      gameStore.setIsBetsClosed(false);
    }
    notificationStore.setNotificationType(responseCode);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
  }

  handleResultReport({ resultReportData }) {
    const { openedCard, currentCardPoint, bankerTotalPoints, playerTotalPoints, cuttingCardPlace } =
      resultReportData;
    if (openedCard) {
      soundService.play(soundTypes.Card);
      cardsStore.updateTableCards(openedCard);
      cardsStore.setCurrentCardPoint(currentCardPoint);
      cardsStore.setBankerTotalPoints(bankerTotalPoints);
      cardsStore.setPlayerTotalPoints(playerTotalPoints);
      cardsStore.setCuttingCardPlace(cuttingCardPlace);
    }
  }

  async handleGetBalance() {
    const {
      data: { result },
    } = await ApiServices.get(ApiServiceTypes.UsersService).getBalance();
    if (result) {
      userInfoStore.setBalance(result.balance);
    }
  }

  handleDoBetResultFunctionality(shuffleCardsInfo, duration) {
    setTimeout(() => {
      if (!applicationStore.isDemo) {
        tableBetHistoryStore.handleBetHistoryUpdate();
      }
      roundInfoStore.setRoundId(null);
      this.handleResetEndedRoundData();
      this.handleFilterResultsOpenedCards();
      if (shuffleCardsInfo) {
        const { cardsResult } = shuffleCardsInfo;
        const lastAndNextResults = cardsResult.reverse().reduce(
          (acc, cur) => {
            if (cur.roundId) {
              acc.alreadyOpenedCards.push(cur);
            } else {
              acc.nextCards.push(cur);
            }
            return acc;
          },
          {
            alreadyOpenedCards: [],
            nextCards: [],
          }
        );
        gameResultStore.setGameResults(lastAndNextResults.nextCards);
        gameResultStore.setAdditionalResults(lastAndNextResults.alreadyOpenedCards);
        this.handleResetEndedRoundData();
        this.handleShuffleStarting(shuffleCardsInfo, true);
      }
    }, duration);
  }

  handleFilterResultsOpenedCards() {
    const alreadyOpenedCards = gameResultStore.gameResults.filter(({ roundId }) => roundId !== 0);
    gameResultStore.setAdditionalResults([
      ...alreadyOpenedCards,
      ...gameResultStore.additionalResults,
    ]);
    const results = [...gameResultStore.gameResults];
    results.length -= alreadyOpenedCards.length;
    gameResultStore.setGameResults(results);
  }

  handleResetEndedRoundData() {
    cardsStore.resetCardsInfo();
    doBetStore.resetBetsModels();
    winningAmountStore.setWinType(null);
    winningAmountStore.setIsPairWin(false);
    winningAmountStore.setWinningAmount(null);
    cardsStore.setIsCuttingCardOpen(false);
    userWinCountStore.setUserWiningBets(null);
    gameStore.setIsBetsClosed(false);
  }

  handleShuffleStarting({ newShuffledCards, burnedCardsInfo }, isResetBetClose) {
    if (isResetBetClose) {
      gameStore.setIsBetsClosed(true);
    }
    const animData = isMobile() ? MobileShuffleAnimation : WebShuffleAnimation;
    cardsStore.setIsShuffle(true);
    // shuffle animation start
    let animationDuration = 0;
    for (let i = 0; i < animData.length; i += 1) {
      shuffleAnimationTimeOutId = setTimeout(() => {
        cardsStore.setShuffleAnimationType(animData[i].payload.current.join(' '));
      }, animationDuration);
      animationsTimeOutId.push(shuffleAnimationTimeOutId);
      animationDuration += animData[i].payload.duration;
    }

    endShuffleAnimationTimeOutId = setTimeout(() => {
      gameResultStore.setGameResults(newShuffledCards.reverse());
      gameResultStore.setAdditionalResults([]);
      roundsWinBetTypes.setWinBetTypes([]);
      cardsStore.setOpenedCardsCount(0);
    }, animationDuration);
    animationsTimeOutId.push(endShuffleAnimationTimeOutId);
    animationDuration += 1000;
    // shuffle animation end

    // burned cards animation start
    cardsStore.setBurnedCardsCount(burnedCardsInfo.burnedCardsCount);
    for (let i = 0; i < burnedCardsInfo.burnedCardsCount + 1; i += 1) {
      animationDuration += 500;
      burnedCardsAnimationTimeOutId = setTimeout(() => {
        soundService.play(soundTypes.Card);
        cardsStore.setShuffleAnimationType('');
        cardsStore.setIsShuffle(false);
        cardsStore.updateBurnedCards(i === 0 ? burnedCardsInfo.firstBurnedCard : null);
      }, animationDuration);
      animationsTimeOutId.push(burnedCardsAnimationTimeOutId);
    }
    animationDuration += 1000;

    endBurnedCardsAnimationTimeOutId = setTimeout(() => {
      cardsStore.setBurnedCards([]);
      cardsStore.setBurnedCardsCount(0);
      if (isResetBetClose) {
        gameStore.setIsBetsClosed(false);
      }
    }, animationDuration);
    animationsTimeOutId.push(endBurnedCardsAnimationTimeOutId);
    // burned cards animation start
  }

  handleResetDataInitial() {
    if (animationsTimeOutId.length) {
      animationsTimeOutId.forEach((id) => {
        clearTimeout(id);
      });
      animationsTimeOutId.length = 0;
    }
    cardsStore.setBurnedCardsCount(0);
    winningAmountStore.setLastWin(0);
    doBetStore.resetBetsModels();
    doBetStore.setReBet(null);
    doBetStore.setReBetAmount(0);
    cardsStore.resetCardsInfo();
    winningAmountStore.setWinType(null);
    winningAmountStore.setIsPairWin(false);
    winningAmountStore.setWinningAmount(null);
    roundsWinBetTypes.setWinBetTypes([]);
    cardsStore.setBurnedCards([]);
    cardsStore.setIsShuffle(false);
    cardsStore.setShuffleAnimationType('');
    cardsStore.setOpenedCardsCount(0);
    gameResultStore.setAdditionalResults([]);
    cardsStore.setIsCuttingCardOpen(false);
    gameStore.setIsBetsClosed(false);
  }
}

export const turboGameService = new TurboGameService();
